import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";

import AddIcon from "@mui/icons-material/Add";

import {PlayerAPI} from "@/api/player";

import {ERROR_OPERATION, dateFormat, playerRoles} from "@/utils/constants";
import {getBirthYears, isDateValid} from "@/utils/functions";
import {useTranslation} from "react-i18next";

const ITEMS_PER_PAGE = 24;

function ScoutingDatabasePage(props) {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const seasonId = useSelector((state) => state?.season?.id);
	const {t} = useTranslation();

	const [loading, setLoading] = useState(false);
	const [players, setPlayers] = useState([]);
	const [totalItems, setTotalItems] = useState();
	const [currentPage, setCurrentPage] = useState(1);
	const [currentSorting, setCurrentSorting] = useState();
	const [currentSortingOrder, setCurrentSortingOrder] = useState();

	// const [birthYears, setBirthYears] = useState([]);

	const [filtersData, setFiltersData] = useState(
		JSON.parse(sessionStorage.getItem("scouting_database_filters")) || {},
	);

	const filters = [
		{
			key: "search",
			label: "Giocatore",
			value: filtersData?.search || "",
			setValue: (value) => {
				sessionStorage.setItem(
					"scouting_database_filters",
					JSON.stringify({search: value}),
				);
				setFiltersData({...filtersData, search: value});
			},
		},
		{
			key: "birthYear",
			label: "Anno di nascita",
			type: "select",
			options: getBirthYears(),
			sorted: false,
			value: filtersData?.birthYear,
			setValue: (value) => {
				sessionStorage.setItem(
					"scouting_database_filters",
					JSON.stringify({birthYear: value}),
				);
				setFiltersData({...filtersData, birthYear: value});
			},
		},
		{
			key: "role",
			label: "Ruolo",
			type: "select",
			options: playerRoles,
			sorted: false,
			value: filtersData?.role,
			setValue: (value) => {
				sessionStorage.setItem(
					"scouting_database_filters",
					JSON.stringify({role: value}),
				);
				setFiltersData({...filtersData, role: value});
			},
		},
	];

	const getPlayers = async () => {
		setLoading(true);
		try {
			const response = await PlayerAPI.paginated({
				page: currentPage,
				pageSize: ITEMS_PER_PAGE,
				prospect: true,
				sorting: currentSorting,
				descending: currentSortingOrder === "descend",
				...filtersData,
				birthYear: filtersData?.birthYear
					? filtersData.birthYear.value
					: undefined,
				role: filtersData?.role ? filtersData.role.value : undefined,
				seasonId,
			});
			setPlayers(response.data);
			setTotalItems(response.totalRecords);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		const delayDebounceFn = setTimeout(() => {
			getPlayers();
		}, 500);

		return () => clearTimeout(delayDebounceFn);
	}, [filtersData, currentPage, currentSorting, currentSortingOrder]);

	const handlePageChanged = (currentPage) => {
		setCurrentPage(currentPage);
	};

	const fromOptionToSorting = (sorting) => {
		switch (sorting) {
			case "firstName":
				return 2;
			case "lastName":
				return 1;
			case "dateOfBirth":
				return 3;
			default:
				return;
		}
	};

	let filteredPlayers = Array.from(players);
	// filteredPlayers = !filtersData?.status
	// 	? filteredPlayers
	// 	: filteredPlayers.filter(
	// 			(fp) => fp.prospectStatus === filtersData.status.value,
	// 		);

	// const groupedPlayers = {};
	// birthYears.forEach((c) => {
	// 	const members = filteredPlayers?.filter(
	// 		(p) =>
	// 			(c === "---" && !p.dateOfBirth) || moment(p.dateOfBirth)?.year() === c,
	// 	);
	// 	groupedPlayers[c] = members;
	// });

	const listConfiguration = {
		data: filteredPlayers,
		onClick: (player) => navigate("/scouting/players/" + player.id),
	};

	const columns = [
		{label: t("name"), key: "firstName", align: "start", sortable: true},
		{label: "Cognome", key: "lastName", align: "start", sortable: true},
		{
			label: "Data di nascita",
			key: "dateOfBirth",
			type: "date",
			align: "center",
			sortable: true,
			sortedByDefault: true,
			sortedOrderByDefault: "descend",
			medium: true,
			customRender: (item) => {
				return isDateValid(item.dateOfBirth)
					? moment(item.dateOfBirth).format(dateFormat)
					: "---";
			},
		},
		{
			label: "Ruolo",
			key: "role",
			align: "start",
			small: true,
			customRender: (row) => {
				return row.goalkeeper ? "P" : row.primaryRole?.[0];
			},
		},
		// {
		// 	label: "Status",
		// 	key: "prospectStatus",
		// 	medium: true,
		// 	customRender: (row) => {
		// 		const status = prospectPlayerStatus?.filter(
		// 			(s) => s.value === row.prospectStatus,
		// 		)?.[0]?.label;

		// 		return status ? (
		// 			<div
		// 				style={{
		// 					border: "1px solid #002160",
		// 					borderRadius: "10px",
		// 					padding: "0px 10px",
		// 				}}
		// 			>
		// 				{status}
		// 			</div>
		// 		) : (
		// 			"---"
		// 		);
		// 	},
		// },
	];

	const headerActions = [
		{
			icon: <AddIcon />,
			title: "Aggiungi",
			onClick: () => navigate("/scouting/players/new"),
		},
	];

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs actions={<MainActions actions={headerActions} />} />
			<CardsContainer
				tableComponent={
					<CardsTable
						fullWidth
						breadcrumbs
						filtersData={filters}
						sortOptions={columns.filter((c) => c.sortable)}
						configurationColumns={columns}
						data={filteredPlayers}
						handleClick={(player) => navigate("/scouting/players/" + player.id)}
						handleSortChange={(option, order) => {
							setCurrentSorting(fromOptionToSorting(option));
							setCurrentSortingOrder(order);
						}}
						totalItems={totalItems}
						itemsPerPage={ITEMS_PER_PAGE}
						handlePageChanged={handlePageChanged}
					/>
				}
				listComponent={
					<CardsList
						type="scouting_database"
						breadcrumbs
						filtersData={filters}
						sortOptions={columns.filter((c) => c.sortable)}
						listConfiguration={listConfiguration}
						handleSortChange={(option, order) => {
							setCurrentSorting(fromOptionToSorting(option));
							setCurrentSortingOrder(order);
						}}
						totalItems={totalItems}
						itemsPerPage={ITEMS_PER_PAGE}
						handlePageChanged={handlePageChanged}
					/>
				}
			/>
		</Page>
	);
}

export default ScoutingDatabasePage;
